import React, { ReactNode } from 'react';
import { Breakpoint } from 'react-socks';
import { common } from '@kurtosys/ksys-app-template';
import { IAppContext } from '@kurtosys/types/helpers/AnalyticsHelper/IAppContext.js';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import { AnalyticsContext, InjectedStyledComponent, KEYS, ResponsiveComponentBase } from '../../../shared/index.js';
import Checkbox from '../../base/Checkbox/Checkbox.js';
import ContextMenu from '../../base/ContextMenu/ContextMenu.js';
import CopyButton from '../../base/CopyButton/CopyButton.js';
import Icon from '../../base/Icon/Icon.js';
import { IIconProps } from '../../base/Icon/models/index.js';
import { ITranslationContextValue } from '../../base/Translate/models/index.js';
import Translate from '../../base/Translate/Translate.js';
import { TranslationContext } from '../../base/Translate/Translate.js';
import VisuallyHidden from '../../base/VisuallyHidden/VisuallyHidden.js';

import { IDocumentDownloadItem } from './models/IDocumentDownloadItem.js';
import { IDocumentDownloadConfiguration, IDocumentDownloadProps, IDocumentDownloadState } from './models/index.js';
import ContextMenuItemIcon from './styledComponents/ContextMenuItemIcon.js';
import DocumentItemWrapper from './styledComponents/DocumentItemWrapper.js';
import DocumentLabel from './styledComponents/DocumentLabel.js';
import DownloadButton from './styledComponents/DownloadButton.js';
import Label from './styledComponents/Label.js';
import Wrapper from './styledComponents/Wrapper.js';
import { DOCUMENT_DOWNLOAD_CATEGORY_KEY, DOCUMENT_DOWNLOAD_COMPONENT_KEY } from './DocumentDownload.meta.js';

const ACCEPT_KEYS = [KEYS.ENTER, KEYS.SPACE, KEYS.SPACEBAR];

export class DocumentDownload extends ResponsiveComponentBase<
	IDocumentDownloadProps,
	IDocumentDownloadState,
	IDocumentDownloadConfiguration
> {
	categoryKey = DOCUMENT_DOWNLOAD_CATEGORY_KEY;
	componentKey = DOCUMENT_DOWNLOAD_COMPONENT_KEY;
	documentDownloadIconKey = 'base.documentDownload.icon';
	documentDownloadSpinnerKey = 'base.documentDownload.spinner';
	documentLabelIconKey = 'base.documentDownload.documentLabelIcon';

	translationContext: ITranslationContextValue | undefined;

	@observable documentDownloadState: IDocumentDownloadState = {};
	analyticsHelper: common.helpers.AnalyticsHelper<IAppContext> | undefined;

	constructor(props: IDocumentDownloadProps) {
		super(props);

		makeObservable(this);
	}

	@computed
	get hasMultipleDocuments(): boolean {
		const { documents } = this.props;
		return !common.commonUtils.isNullOrEmpty(documents) && documents.length > 1;
	}

	@computed
	get ariaLabelPrefix(): string {
		const { ariaLabelPrefix } = this.props;
		if (ariaLabelPrefix) {
			return `${this.translate(ariaLabelPrefix)}, `;
		}
		return '';
	}

	handleItemToggle = (id: string, selected: boolean) => {
		const { onItemToggle } = this.props;
		if (onItemToggle) {
			onItemToggle(id, selected);
		}
	};

	setItemDownloading(id: string) {
		this.toggleItemDownloading(id, true);
	}

	completeItemDownloading(id: string) {
		this.toggleItemDownloading(id, false);
	}

	@action
	toggleItemDownloading(id: string, isDownloading: boolean) {
		this.documentDownloadState[id] = isDownloading;
		this.documentDownloadState = Object.assign({}, this.documentDownloadState);
	}

	handleDownloadClick = async (docId: string, payload: any) => {
		const { onDownloadClick } = this.props;
		const canDownload = !this.isDownloading(docId);
		const analyticsHelper = this.analyticsHelper;

		if (onDownloadClick && canDownload) {
			this.setItemDownloading(docId);
			const result = await onDownloadClick(payload);
			if (result) {
				this.completeItemDownloading(docId);
			}
		}

		// If there is no click event then log the default context, else assume that the on click will log
		if (!onDownloadClick && analyticsHelper) {
			analyticsHelper.logEvent({
				event: 'document_click',
				eventType: 'click',
				context: {},
			});
		}
	};

	isDownloading(id: string) {
		return this.documentDownloadState && this.documentDownloadState[id];
	}

	handleKeyDown = (event: React.KeyboardEvent<HTMLElement>, callbackFn: () => void) => {
		const { key } = event;
		if (callbackFn && ACCEPT_KEYS.indexOf(key as KEYS) !== -1) {
			callbackFn();
		}
	};

	handleDocumentItemLabel = (document: IDocumentDownloadItem<any>) => {
		const { allowSelection } = this.props;
		if (!allowSelection) {
			this.handleDownloadClick(document.id, document.payload);
		} else {
			this.handleItemToggle(document.id, !document.selected);
		}
	};

	translate(ariaLabelText: string, placeholders: Record<string, any> = {}) {
		if (this.translationContext) {
			return this.translationContext.translate(ariaLabelText, placeholders);
		}
		return common.commonUtils.replacePlaceholders({ values: placeholders, text: ariaLabelText });
	}

	getCopyButton(document: IDocumentDownloadItem<any>) {
		const { showCopyButton, copyButtonProps = {} } = this.props;
		if (showCopyButton && document) {
			const payload = document.href || copyButtonProps.payload;
			return (
				<CopyButton
					{...copyButtonProps}
					payload={payload}
					aria-label={this.translate(`{documentCopyUrlPrefix} Copy URL "{payload}"`, {
						payload,
						documentCopyUrlPrefix: `${this.ariaLabelPrefix} ${this.translate(document.label)}`,
					})}
				/>
			);
		}
	}

	/**
	 *
	 * Should be an anchor tag with a download attribute
	 *
	 * @param document
	 * @param useSpinner
	 * @returns
	 */
	getDownloadButton(document: IDocumentDownloadItem<any>, useSpinner = true) {
		const iconAsset =
			this.isDownloading(document.id) && useSpinner
				? this.documentDownloadSpinnerKey
				: this.documentDownloadIconKey;

		const icon = <Icon asset={iconAsset} />;

		return (
			<DownloadButton
				aria-label={this.getDocumentDownloadAriaLabel(document)}
				onClick={() => this.handleDownloadClick(document.id, document.payload)}
				onKeyDown={(e: any) =>
					this.handleKeyDown(e, () => this.handleDownloadClick(document.id, document.payload))
				}
			>
				{icon}
			</DownloadButton>
		);
	}

	/**
	 * Renders the label of a document in the DocumentDownload component.
	 * If allowSelection prop is true, it returns a Checkbox component.
	 * Otherwise, it returns a Label component with the translated document label.
	 *
	 * @param document - The document object containing information about the document.
	 * @returns The rendered label component for the document.
	 */
	getDocumentLabel(document: IDocumentDownloadItem<any>) {
		const { allowSelection, hideLabel } = this.props;

		if (allowSelection) {
			return (
				<Checkbox
					label={document.label}
					value={document.selected}
					onChange={() => this.handleItemToggle(document.id, !document.selected)}
				/>
			);
		}

		const label = (
			<Label>
				<Translate>{document.label}</Translate>
			</Label>
		);

		if (hideLabel) {
			return <VisuallyHidden>{label}</VisuallyHidden>;
		}

		return label;
	}

	getDocumentDownloadAriaLabel(document: IDocumentDownloadItem<any>) {
		return this.translate(`{documentDownloadPrefix} Download {documentLabel}`, {
			documentLabel: document.label,
			documentDownloadPrefix: this.ariaLabelPrefix,
		});
	}

	/**
	 * Renders a single document item in the DocumentDownload component.
	 * Generates the necessary JSX elements based on the provided props and handles the click events for downloading or toggling the document item.
	 *
	 * @param props - An object containing the following properties:
	 *   - documents (array): An array of document objects, where each object represents a single document item. Each document object should have the following properties:
	 *     - id (string): The unique identifier for the document.
	 *     - label (string): The label or name of the document.
	 *     - payload (any): The payload or data associated with the document.
	 *     - selected (boolean): Indicates whether the document is selected or not.
	 *   - useSpinner (boolean, optional): Indicates whether to display a spinner icon while the document is being downloaded. Default is true.
	 *   - iconPosition (string, optional): The position of the download and copy buttons relative to the document label. Can be either 'before' or 'after'. Default is 'after'.
	 * @returns The JSX element representing the single document item.
	 */
	singleDocument(props: IDocumentDownloadProps): ReactNode {
		const { documents, useSpinner = true, iconPosition = 'after' } = props;
		if (documents && documents.length === 1) {
			const document = documents[0];
			const label = this.getDocumentLabel(document);
			const downloadButton = this.getDownloadButton(document, useSpinner);
			const copyButton = this.getCopyButton(document);
			return (
				<DocumentItemWrapper key={document.id}>
					{iconPosition === 'before' && copyButton}
					{iconPosition === 'before' && downloadButton}
					<DocumentLabel onClick={() => this.handleDownloadClick(document.id, document.payload)}>
						{label}
					</DocumentLabel>
					{iconPosition === 'after' && downloadButton}
					{iconPosition === 'after' && copyButton}
				</DocumentItemWrapper>
			);
		}
	}

	multipleDocuments(props: IDocumentDownloadProps): ReactNode {
		const {
			documents,
			availableLabel,
			selectionLabel,
			allowSelection,
			showPill,
			hideLabel,
			dropdownLabelType = 'text',
			pillProps,
			useSpinner = true,
			iconPosition = 'after',
			describedById,
		} = props;

		if (documents && this.hasMultipleDocuments) {
			const selectedDocuments = documents.filter((document: IDocumentDownloadItem) => document.selected);
			let label = availableLabel || '';
			if (allowSelection && selectionLabel && selectedDocuments && selectedDocuments.length > 0) {
				label = selectionLabel;
			}
			const values = {
				selected: selectedDocuments.length,
				available: documents.length,
			};

			label = this.translate(label, values);

			if (showPill) {
				if (allowSelection) {
					label = values.selected.toString();
				} else {
					label = values.available.toString();
				}
			} else if (hideLabel) {
				label = '';
			}

			let labelIcon;
			if (dropdownLabelType === 'icon') {
				labelIcon = {
					asset: this.documentLabelIconKey,
				} as IIconProps;
			}

			const contextMenuComponent = (
				<ContextMenu
					label={label}
					labelIcon={labelIcon}
					showPill={showPill}
					pillProps={pillProps}
					isMultiSelect={true}
					onChildSelection={(e: any, index: number) => {
						const document = documents[index];
						if (allowSelection) {
							e.stopPropagation();
							e.preventDefault();
							this.handleItemToggle(document.id, !document.selected);
						} else {
							this.handleDownloadClick(document.id, document.payload);
						}
					}}
					describedById={describedById}
				>
					{documents.map((document: IDocumentDownloadItem, index: number) => {
						const documentLabel = this.getDocumentLabel(document);
						const downloadButtonComponent = this.getDownloadButton(document, useSpinner);
						// Backwards compatibility for styling
						const downloadButton = <ContextMenuItemIcon>{downloadButtonComponent}</ContextMenuItemIcon>;
						const copyButton = this.getCopyButton(document);

						return (
							<DocumentItemWrapper key={document.id}>
								{iconPosition === 'before' && copyButton}
								{iconPosition === 'before' && downloadButton}
								{documentLabel}
								{iconPosition === 'after' && downloadButton}
								{iconPosition === 'after' && copyButton}
							</DocumentItemWrapper>
						);
					})}
				</ContextMenu>
			);

			return contextMenuComponent;
		}
	}

	render() {
		const breakpoints = this.breakpoints;

		// Add observable to render in order for state changes to force renders
		this.documentDownloadState;

		return (
			<AnalyticsContext.Consumer>
				{(analyticsHelper) => {
					this.analyticsHelper = analyticsHelper;
					return (
						<TranslationContext.Consumer>
							{(translationContext) => {
								this.translationContext = translationContext;
								return (
									<>
										{breakpoints.map((breakpoint, breakpointIndex) => {
											const { props } = breakpoint;
											if (props) {
												const breakpointProps = this.getBreakpointComponentProps(
													breakpoint,
													breakpointIndex,
												);
												const { className } = props;
												return (
													<Breakpoint {...breakpointProps}>
														<Wrapper className={className}>
															{this.hasMultipleDocuments
																? this.multipleDocuments(
																		props as IDocumentDownloadProps,
																  )
																: this.singleDocument(props as IDocumentDownloadProps)}
														</Wrapper>
													</Breakpoint>
												);
											}
										})}
									</>
								);
							}}
						</TranslationContext.Consumer>
					);
				}}
			</AnalyticsContext.Consumer>
		);
	}
}

export default InjectedStyledComponent(
	observer(DocumentDownload),
	DOCUMENT_DOWNLOAD_CATEGORY_KEY,
	DOCUMENT_DOWNLOAD_COMPONENT_KEY,
);
